import { chunkIds, fetchAllRows } from './fetch-all-rows';
import { consumeEntityCode } from './entity-codes';
import type { TypedSupabaseClient } from '../client';
import type {
  ArticleAttributeRow,
  ArticleOptionValue,
  ArticleVariant,
  ArticleVariantMatrix,
  IArticleVariantRepository,
  SaveArticleVariantsInput,
} from '../../../interfaces/src';

// The variant tables and the Phase 3 write RPC are intentionally absent from
// the hand-maintained generated database types. Keep that escape hatch narrow:
// reads still use authorized views, while the only write is the atomic RPC.
type UntypedFrom = { from: (table: string) => any };
type UntypedRpc = {
  rpc: (
    functionName: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: unknown }>;
};

const READ_OPTION_TYPES = 'authorized_article_option_types';
const READ_OPTION_VALUES = 'authorized_article_option_values';
const READ_VARIANTS = 'authorized_article_variants';
const READ_VARIANT_VALUES = 'authorized_article_variant_values';

type MatrixReadSources = {
  types: string;
  values: string;
  variants: string;
  junction: string;
};

const STAFF_MATRIX_SOURCES: MatrixReadSources = {
  types: READ_OPTION_TYPES,
  values: READ_OPTION_VALUES,
  variants: READ_VARIANTS,
  junction: READ_VARIANT_VALUES,
};
const PUBLIC_MATRIX_SOURCES: MatrixReadSources = {
  types: 'storefront_catalog_article_option_types',
  values: 'storefront_catalog_article_option_values',
  variants: 'storefront_catalog_article_variants',
  junction: 'storefront_catalog_article_variant_values',
};

export class SupabaseArticleVariantRepository implements IArticleVariantRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  private sb(): UntypedFrom {
    return this.supabase as unknown as UntypedFrom;
  }

  async getMatrix(articleId: string): Promise<ArticleVariantMatrix> {
    const all = await this.listForArticles([articleId]);
    return all[articleId] ?? { optionTypes: [], variants: [] };
  }

  async listForArticles(
    articleIds: string[],
  ): Promise<Record<string, ArticleVariantMatrix>> {
    return this.readMatrices(articleIds, STAFF_MATRIX_SOURCES);
  }

  async getPublicMatrix(
    articleId: string,
    tenantId: string,
  ): Promise<ArticleVariantMatrix> {
    const all = await this.listPublicForArticles([articleId], tenantId);
    return all[articleId] ?? { optionTypes: [], variants: [] };
  }

  async listPublicForArticles(
    articleIds: string[],
    tenantId: string,
  ): Promise<Record<string, ArticleVariantMatrix>> {
    // Never infer shopper access from an auth session: public catalogue views
    // deliberately serve the same projection to guests and logged-in shoppers.
    if (!tenantId) return {};
    return this.readMatrices(articleIds, PUBLIC_MATRIX_SOURCES, tenantId);
  }

  private async readMatrices(
    articleIds: string[],
    sources: MatrixReadSources,
    tenantId?: string,
  ): Promise<Record<string, ArticleVariantMatrix>> {
    const result: Record<string, ArticleVariantMatrix> = {};
    for (const id of articleIds) result[id] = { optionTypes: [], variants: [] };
    if (articleIds.length === 0) return result;

    const [types, variants] = await Promise.all([
      this.readRows(sources.types, 'article_id', articleIds, tenantId),
      this.readRows(sources.variants, 'article_id', articleIds, tenantId),
    ]);
    const [valueRows, junction] = await Promise.all([
      this.readRows(sources.values, 'option_type_id', types.map((row) => row['id'] as string)),
      this.readRows(sources.junction, 'variant_id', variants.map((row) => row['id'] as string), undefined, true),
    ]);

    const valuesByType = new Map<string, ArticleOptionValue[]>();
    for (const v of valueRows) {
      const typeId = v['option_type_id'] as string;
      const list = valuesByType.get(typeId) ?? [];
      list.push({
        id: v['id'] as string,
        optionTypeId: typeId,
        value: (v['value'] as string) ?? '',
        swatch: (v['swatch'] as string) ?? null,
        displayOrder: (v['display_order'] as number) ?? 0,
      });
      valuesByType.set(typeId, list);
    }

    const valueIdsByVariant = new Map<string, string[]>();
    for (const j of junction) {
      const variantId = j['variant_id'] as string;
      const list = valueIdsByVariant.get(variantId) ?? [];
      list.push(j['option_value_id'] as string);
      valueIdsByVariant.set(variantId, list);
    }

    for (const t of types) {
      const articleId = t['article_id'] as string;
      const matrix = result[articleId];
      if (!matrix) continue;
      matrix.optionTypes.push({
        id: t['id'] as string,
        articleId,
        name: (t['name'] as string) ?? '',
        displayOrder: (t['display_order'] as number) ?? 0,
        values: valuesByType.get(t['id'] as string) ?? [],
      });
    }

    for (const v of variants) {
      const articleId = v['article_id'] as string;
      const matrix = result[articleId];
      if (!matrix) continue;
      matrix.variants.push(
        mapVariant(v, valueIdsByVariant.get(v['id'] as string) ?? []),
      );
    }

    for (const matrix of Object.values(result)) {
      matrix.optionTypes.sort((a, b) => a.displayOrder - b.displayOrder || a.id.localeCompare(b.id));
      for (const option of matrix.optionTypes) option.values.sort((a, b) => a.displayOrder - b.displayOrder || a.id.localeCompare(b.id));
      matrix.variants.sort((a, b) => a.displayOrder - b.displayOrder || a.id.localeCompare(b.id));
    }
    return result;
  }

  /**
   * Flatten every option value in the tenant into `{article, name, value, swatch}`.
   *
   * Two reads and an in-memory join rather than a view: the aggregation that
   * turns these rows into the attribute library is domain logic, and keeping
   * this a plain read is what lets the Spring Boot adapter reproduce it.
   */
  async listAttributeRows(): Promise<ArticleAttributeRow[]> {
    const types = await this.readRows(READ_OPTION_TYPES);
    if (types.length === 0) return [];
    const valueRows = await this.readRows(READ_OPTION_VALUES, 'option_type_id', types.map((row) => row['id'] as string));

    const typeById = new Map(
      types.map((t) => [
        t['id'] as string,
        {
          articleId: t['article_id'] as string,
          name: (t['name'] as string) ?? '',
        },
      ]),
    );

    const rows: ArticleAttributeRow[] = [];
    for (const v of (valueRows ?? []) as Record<string, unknown>[]) {
      const type = typeById.get(v['option_type_id'] as string);
      if (!type) continue;
      rows.push({
        articleId: type.articleId,
        name: type.name,
        value: (v['value'] as string) ?? '',
        swatch: (v['swatch'] as string) ?? null,
      });
    }
    return rows;
  }

  /** Page every authorized read and bound IN URLs; a truncated matrix can
   * otherwise make a variant parent look like a selectable ordinary article. */
  private async readRows(source: string, column?: string, ids?: string[], tenantId?: string, junction = false): Promise<Record<string, unknown>[]> {
    const rows: Record<string, unknown>[] = [];
    for (const chunk of ids ? chunkIds([...new Set(ids)]) : [undefined]) {
      rows.push(...await fetchAllRows<Record<string, unknown>>((from, to) => {
        let query = this.sb().from(source).select('*');
        if (column && chunk) query = query.in(column, chunk);
        if (tenantId) query = query.eq('tenant_id', tenantId);
        query = junction ? query.order('variant_id').order('option_value_id') : query.order('id');
        return query.range(from, to);
      }));
    }
    return rows;
  }

  async saveMatrix(
    articleId: string,
    input: SaveArticleVariantsInput,
  ): Promise<ArticleVariantMatrix> {
    const client = this.supabase as unknown as UntypedRpc;
    // A variant that asked for the next barcode gets it HERE, from the same
    // tenant counter and the same helper as a new article: the RPC only ever
    // receives concrete values. Sequential on purpose — the numbers follow
    // the order of the grid.
    const generatedBarcodes = new Map<number, string>();
    for (const [index, variant] of input.variants.entries()) {
      if (variant.autoGenerateBarcode && !variant.barcode?.trim()) {
        generatedBarcodes.set(index, await consumeEntityCode(this.supabase, 'barcode'));
      }
    }
    const matrix = {
      option_types: input.optionTypes.map((optionType, typeIndex) => ({
        id: optionType.id || null,
        name: optionType.name,
        display_order: optionType.displayOrder ?? typeIndex,
        values: optionType.values.map((optionValue, valueIndex) => ({
          id: optionValue.id || null,
          value: optionValue.value,
          swatch: optionValue.swatch ?? null,
          display_order: optionValue.displayOrder ?? valueIndex,
        })),
      })),
      variants: input.variants.map((variant, variantIndex) => ({
        id: variant.id || null,
        sku: variant.sku ?? null,
        barcode: generatedBarcodes.get(variantIndex) ?? variant.barcode ?? null,
        sale_price_ht: variant.salePriceHt ?? null,
        sale_price_ttc: variant.salePriceTtc ?? null,
        // Absent key = keep the stored value: the RPC only touches the
        // purchase price when the caller sends it (see SaveArticleVariantInput).
        ...(variant.purchasePriceHt !== undefined
          ? { purchase_price_ht: variant.purchasePriceHt }
          : {}),
        initial_stock: variant.initialStock ?? 0,
        // Absent key = leave the stock alone. The server reconciles the
        // ledger with this target; `initial_stock` stays the declaration.
        ...(variant.stockQuantity !== undefined
          ? { stock_quantity: variant.stockQuantity }
          : {}),
        image_url: variant.imageUrl ?? null,
        is_active: variant.isActive ?? true,
        display_order: variant.displayOrder ?? variantIndex,
        option_value_ids: variant.optionValueIds ?? [],
        option_value_refs: (variant.optionValueRefs ?? []).map((reference) => ({
          type_index: reference.typeIndex,
          value_index: reference.valueIndex,
        })),
      })),
    };
    const { error } = await client.rpc('save_article_variant_matrix_atomic', {
      p_article_id: articleId,
      p_matrix: matrix,
    });
    if (error) throw error;

    return this.getMatrix(articleId);
  }
}

function mapVariant(
  row: Record<string, unknown>,
  optionValueIds: string[],
): ArticleVariant {
  return {
    id: row['id'] as string,
    articleId: row['article_id'] as string,
    sku: (row['sku'] as string) ?? null,
    barcode: (row['barcode'] as string) ?? null,
    salePriceHt: (row['sale_price_ht'] as number) ?? null,
    salePriceTtc: (row['sale_price_ttc'] as number) ?? null,
    // Missing before the purchase-price migration, and masked (null) for a
    // role without costs.view — both read as "inherit the article's".
    purchasePriceHt: (row['purchase_price_ht'] as number) ?? null,
    initialStock: Number(row['initial_stock'] ?? 0),
    // Older projections without the ledger column fall back to the declaration.
    stockQuantity: Number(row['stock_quantity'] ?? row['initial_stock'] ?? 0),
    imageUrl: (row['image_url'] as string) ?? null,
    isActive: (row['is_active'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 0,
    optionValueIds,
  };
}
